import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'

import { deleteClassApi } from 'core/application/adminDashboard/benefitDetails/design/deleteClass'
import { getProductClassWizardState } from 'core/application/adminDashboard/benefitDetails/design/groupHra/productClass/benefitDesignClasses'
import { getProductClassDesignWizard } from 'core/application/adminDashboard/benefitDetails/design/groupHra/productClass/wizard'
import { finishBenefitDesignApi } from 'core/application/adminDashboard/benefitDetails/finishBenefitDesign'
import getProductClassDetails from 'core/application/adminDashboard/benefitDetails/getProductClassDetails'
import benefitDesignStateApi, {
  BenefitDesignStateValues,
  ProductClassValues,
} from 'core/application/adminDashboard/benefitDetails/groupHra/benefitDesignState'
import { ProductClass } from 'core/domain/benefit/productClass'
import { WizardStatus } from 'core/domain/wizard/wizard'

import Tooltip from 'common/components/dataDisplay/Tooltip'
import Modal from 'common/components/feedback/Modal'
import notification from 'common/components/feedback/Notification'
import Button from 'common/components/general/Button'
import Icon from 'common/components/general/Icon'

import { commandRequest } from 'common/RequestUtils'
import { Col, Row, Skeleton } from 'common/components'

import { ROUTE_PATHS } from '../../../../../common/routePaths'
import ProductClassSummary from '../../../common/ProductClassSummaryWrapper'
import ProductClassWizardModal from '../../../common/productClassWizardModal'
import { BaseComponentProps } from '../../common/models'
import { areDataValid, getAlertContentBasedOnDesignStatus, showDeleteAsDisabled } from './Helper'
import { productClassSummary } from './ProductClassSummaryContent'
import { NavItemsMapping } from './productClassWizard/ProductClassStepsMapping'

import '../../common/DesignStyles.less'

export interface ClassDesignProps {
  classes: ProductClassValues[]
  sameClassForAll: boolean
  status: WizardStatus
}

export interface ProductClassModalStateProps {
  productId?: string
  visible: boolean
  editingStep?: string
}

const ClassDesign: React.FC<BaseComponentProps<ClassDesignProps>> = (props) => {
  const { bindSubmitFormHandler } = props
  const [productClasses, setProductClasses] = useState<ProductClassValues[] | undefined>(props.state.classes)
  const [productClassModalState, setProductClassModalState] = useState<ProductClassModalStateProps>({
    visible: false,
  })
  let history = useHistory()
  const managePeopleFullPath = `${ROUTE_PATHS.MANAGE_PEOPLE}`
  const { t: translate } = useTranslation()
  const { planPeriod } = useParams<any>()

  useEffect(() => {
    props.setIsFormValid(areDataValid(props.state.sameClassForAll, productClasses))
    defineNextTooltipContent(props.state.sameClassForAll, productClasses)
  }, [productClasses, props])

  const onSubmit = () => {
    Modal.confirm({
      title: translate('admin-dashboard.benefit-details.class-design.on-submit-alert-header'),
      content: (
        <span>
          <p>{translate('admin-dashboard.benefit-details.class-design.on-submit-alert-content-1')}</p>
          <p className="italic">
            {translate('admin-dashboard.benefit-details.class-design.on-submit-alert-content-2')}
          </p>
        </span>
      ),
      async onOk() {
        const res = await commandRequest(() => finishBenefitDesignApi(planPeriod))
        if (!res.errors) {
          props.finish()
          history.push(`${ROUTE_PATHS.DASHBOARD_PATH}`)
        } else {
          props.setIsSubmiting(false)
          notification.open({
            message: res.errors[0],
            type: 'error',
          })
        }
      },
      onCancel() {
        props.setIsSubmiting(false)
      },
      okText: translate('admin-dashboard.benefit-details.class-design.ok-text'),
      okType: 'primary',
      cancelText: translate('admin-dashboard.benefit-details.class-design.cancel-text'),
    })
  }

  const getDesignClassesContent = (): ReactNode => {
    if (props.state.sameClassForAll) {
      return getSameClassContent()
    } else {
      return getMultipleClassesContent()
    }
  }

  const getSameClassContent = (): ReactNode => {
    return (
      <Row className="pt-16">
        <Col md={24}>
          {productClasses && productClasses.length > 0 ? (
            <ProductClassSummary
              title={productClasses[0].name}
              id={productClasses[0].id}
              apiEndpoint={getProductClassDetails}
              extra={
                productClasses[0].status !== WizardStatus.Completed ? (
                  <Tooltip
                    title={translate('admin-dashboard.benefit-details.class-design.continue-benefit-tooltip')}
                    position="bottom"
                  >
                    <Icon type="info-circle" />{' '}
                  </Tooltip>
                ) : null
              }
              designSummary={(productClass: any) =>
                productClassSummary(productClass, setProductClassModalState, translate)
              }
              loadDataAfterRender
              showComplete={productClasses[0].status !== WizardStatus.Completed}
              onComplete={() => {
                setProductClassModalState({
                  visible: true,
                  productId: productClasses[0].id,
                })
              }}
            />
          ) : null}
          {productClasses && productClasses.length === 0 ? (
            <Button
              type="dashed"
              htmlType="button"
              icon="fa fa-plus"
              className="full-width w-100 t-design-class"
              onClick={() => {
                setProductClassModalState({
                  visible: true,
                })
              }}
            >
              Design class
            </Button>
          ) : null}
        </Col>
      </Row>
    )
  }

  const getMultipleClassesContent = (): ReactNode => {
    let content = null
    if (productClasses && productClasses.length > 0) {
      const productClassesContent = productClasses.map((productClass: ProductClassValues, index: number) => {
        return (
          <Row className="pt-16" key={index}>
            <Col md={24}>
              <ProductClassSummary
                title={productClass.name}
                id={productClass.id}
                loadDataAfterRender={productClasses.length === 1}
                apiEndpoint={getProductClassDetails}
                extra={
                  productClass.status !== WizardStatus.Completed ? (
                    <Tooltip
                      title={translate('admin-dashboard.benefit-details.class-design.continue-benefit-tooltip')}
                      position="bottom"
                    >
                      <Icon type="info-circle" />{' '}
                    </Tooltip>
                  ) : null
                }
                designSummary={(item: ProductClass | null) =>
                  productClassSummary(item, setProductClassModalState, translate)
                }
                showComplete={productClass.status !== WizardStatus.Completed}
                onComplete={() => {
                  setProductClassModalState({
                    visible: true,
                    productId: productClass.id,
                  })
                }}
                delete={{
                  disabled: showDeleteAsDisabled(props.state.status, productClass.status, productClasses),
                  tooltip: 'You can not delete the class if there are only two of them with status completed',
                  onClick: () => {
                    if (productClass.hasEmployeesAssigned) {
                      confirmWhenClassHasEmployeesAssigned(productClass)
                    } else {
                      confirmWhenApprovingDeletionOfClass(productClass)
                    }
                  },
                }}
              />
            </Col>
          </Row>
        )
      })
      content = (
        <>
          <Row className="pt-24">
            <Col>
              <h5 className="your_classes_label">Your classes:</h5>
            </Col>
          </Row>
          {productClassesContent}
        </>
      )
    }
    return (
      <>
        {content}
        <Row className="pt-16">
          <Col span={24}>
            <Button
              type="dashed"
              htmlType="button"
              icon="fa fa-plus"
              className="full-width w-100 t-design-class"
              onClick={() => {
                setProductClassModalState({
                  visible: true,
                })
              }}
            >
              {translate('admin-dashboard.benefit-details.class-design.no-classes-button')}
            </Button>
          </Col>
        </Row>
      </>
    )
  }

  const defineNextTooltipContent = (sameClassForAll: boolean, classes: ProductClassValues[] | undefined): void => {
    if (sameClassForAll === false) {
      props.setOverrideTooltip(
        translate('admin-dashboard.benefit-details.class-design.multiple-classes-finish-tooltip'),
      )
    }
  }

  const confirmWhenClassHasEmployeesAssigned = (productClass: ProductClassValues): void => {
    Modal.confirm({
      title: translate('admin-dashboard.benefit-details.class-design.cannot-delete-class-alert-header'),
      content: translate('admin-dashboard.benefit-details.class-design.cannot-delete-class-alert-msg'),
      onOk() {},
      onCancel() {
        history.push(managePeopleFullPath)
      },
      okText: translate('admin-dashboard.benefit-details.class-design.cannot-delete-class-ok-text'),
      okType: 'primary',
      cancelText: translate('admin-dashboard.benefit-details.class-design.cannot-delete-class-cancel-text'),
    })
  }

  const confirmWhenApprovingDeletionOfClass = (productClass: ProductClassValues): void => {
    Modal.confirm({
      title: translate('admin-dashboard.benefit-details.class-design.delete-class-alert-header'),
      content: translate('admin-dashboard.benefit-details.class-design.delete-class-alert-msg'),
      async onOk() {
        const deleteResponse = await commandRequest(() =>
          deleteClassApi({
            productClassId: productClass.id,
            planPeriodId: planPeriod,
          }),
        )
        if (deleteResponse.errors) {
          notification.open({
            message: deleteResponse.errors[0],
            type: 'error',
          })
        } else {
          refreshDesignClassesState()
        }
      },
      okText: translate('admin-dashboard.benefit-details.class-design.delete-class-alert-ok-text'),
      okType: 'primary',
    })
  }

  const refreshDesignClassesState = (): void => {
    props.setIsFormValid(false)
    setProductClassModalState({
      visible: false,
    })
    setProductClasses(undefined)
    benefitDesignStateApi(planPeriod).then((response: BenefitDesignStateValues) => {
      setProductClasses(response.designClasses)
      props.setIsFormValid(areDataValid(props.state.sameClassForAll, response.designClasses))
      defineNextTooltipContent(props.state.sameClassForAll, response.designClasses)
    })
  }

  bindSubmitFormHandler(onSubmit)

  return (
    <>
      {productClassModalState.visible ? (
        <ProductClassWizardModal
          getProductClassWizard={(planPeriodId: string, productId?: string) =>
            getProductClassDesignWizard(planPeriodId, productId)
          }
          getProductClassWizardState={(planPeriodId: string, productId?: string) =>
            getProductClassWizardState(planPeriodId, productId)
          }
          visible={productClassModalState.visible}
          id={productClassModalState.productId}
          planPeriodId={planPeriod}
          afterClose={() => {
            refreshDesignClassesState()
          }}
          editingStep={productClassModalState.editingStep}
          navigationMapping={NavItemsMapping}
        />
      ) : null}
      <div className="design_classes">
        <Row justify="center">
          <Col sm={24} md={20} lg={18} className="benefit_design_page_header">
            <h5>{translate('admin-dashboard.benefit-details.class-design.header')}</h5>
          </Col>
        </Row>
        <Skeleton active loading={!productClasses}>
          <Row justify="center">
            <Col sm={24} md={20} lg={18}>
              {getAlertContentBasedOnDesignStatus(props.state.sameClassForAll, productClasses, translate)}
            </Col>
          </Row>
          <Row justify="center">
            <Col sm={24} md={20} lg={18}>
              {getDesignClassesContent()}
            </Col>
          </Row>
        </Skeleton>
      </div>
    </>
  )
}

export default ClassDesign
